import React, { useState, useMemo, useEffect } from "react";
import Modal from "../Modal";
import styled from '@emotion/styled';
import SelectWithLabel from "../../Inputs/Select";
import { isMobile } from 'react-device-detect';
import EyeTicketLogo from '../../../assets/EyeTicket_Logo.png';
import TextInputWithLabel from "../../Inputs/TextInput";
import Table, { SelectColumnFilter } from "../Table";
import { Alert } from "../Alert";
import { fetchAccount, fetchTitles } from "../../../http/account";
import { fetchUserFrames } from "../../../http/frames";
import CheckboxWithLabel from "../../Inputs/Checkbox";
import { updateEyeTicket, createEyeTicket } from "../../../http/eyeticket";
import { fetchLinkedAccounts } from "../../../http/account";
import { fetchFavouriteOpticians, fetchOpticiansByPostcode, addFavouriteOptician, removeFavouriteOptician } from "../../../http/optician";
import Card from "../Card";
import { StyledModal } from "../../StyledComponents/Modals/Modals";

const Logo = styled.img`
  height: auto;
  width: 120px;
  margin-right: 20px;
`;

const CenterTextContainer = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right:130px;
`;

const StepsHeader = styled.div`
  background-color: ${({ backGroundColour }) => backGroundColour};
  color: ${({ fontColour }) => fontColour};
  display: flex;
  align-items: center;
  padding: 20px;
  box-shadow: 0 4px 4px -2px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const Step = styled.div`
  width: 30px;
  height: 30px;
  line-height: 30px;
  border-radius: 15px;
  background: #cccccc;
  color: white;
  text-align: center;
  font-size: 0.9em;
`;

const ActiveStep = styled(Step)`
  background-color: #4CAF50;
`;

const StepLabel = styled.span`
  display: inline-block;
  padding: 0 ${isMobile ? '10px' : '15px'};;
  font-weight: bold;
  color: ${({ fontColour }) => fontColour};
`;

const StepContainer = styled.div`
  display: flex;
  align-items: center;
  ${(props) => (props.right ? "right: 20px;" : "left: 20px;")}
`;

const ModalBody = styled.div`
  max-height: ${isMobile ? '500px' : '700px'};
  overflow-y: auto;
  width:100%;

  padding: 0px 40px ${isMobile ? "0px" : "30px"} 40px;

  flex-grow: 1;
  & > div { 
    margin-top: 0;
  }
`;

const ModalFooter = styled.div`
  text-align: right;
  flex-shrink: 0;
  padding: 10px 0px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;

const FieldContainer = styled.div`
  flex: 1;
  min-width: 120px;
  
  &:first-of-type {
    margin-left: 0;
  }
`;

const Button = styled.button`
  background-color: #93C572;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 10px 20px;
  margin-right: 10px;

  &:hover {
    background-color: #698D51;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  margin-right: 10px;
  border-radius: 5px;
  cursor: pointer;
  &:hover {
    background-color: #5a6268;
  }
`;

const Heading = styled.h3`
  margin: 0;
  margin-top: 10px;
  padding-bottom: 5px;
`;

const Paragraph = styled.p`
  margin: 0 0 20px 0;
  font-size:15px;
`;

const FlexContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
`;

export default function OpticiansModal({
  showHideModal,
  isModalOpen,
  email,
  accountCode,
  companyName,
  colours }) {

  const [isLoading, setIsLoading] = useState(false);
  const [account, setAccount] = useState({});
  const [error, setError] = useState(null);
  const [opticians, setOpticians] = useState([]);
  const [postcode, setPostcode] = useState('WS98BZ');
  const [isValidPostcode, setIsValidPostcode] = useState(true);

  useEffect(() => {
    const loadAccount = async () => {
      setIsLoading(true);
      try {
        const fetchedAccount = await fetchAccount();
        setAccount(fetchedAccount.Users_Profile);
        setPostcode((fetchedAccount.Users_Profile.copostcode ? fetchedAccount.Users_Profile.copostcode : 'WS98BZ'));

        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    loadAccount();
  }, [showHideModal]);

  const loadOpticians = async () => {
    if (postcode) {
      try {
        const opticiansResponse = await fetchOpticiansByPostcode(postcode, account.dispensing_Fee_Max);
        const favouriteOpticiansResponse = await fetchFavouriteOpticians(account.dispensing_Fee_Max);

        if (opticiansResponse.length > 0) {
          setIsLoading(true);
          const favouriteOpticians = favouriteOpticiansResponse.map(optician => ({
            ...optician,
            type: "Favourite"
          }));

          const opticians = opticiansResponse.map(optician => ({
            ...optician,
            type: "Local"
          }));

          const mergedOpticians = [...opticians, ...favouriteOpticians];

          const opticianMap = new Map();
          mergedOpticians.forEach(optician => {
            const existing = opticianMap.get(optician.unqID);
            if (!existing || (existing && existing.type == "Local" && optician.type == "Favourite")) {
              opticianMap.set(optician.unqID, optician);
            }
          });

          const uniqueMergedOpticians = Array.from(opticianMap.values());
          setOpticians(uniqueMergedOpticians);
          setIsValidPostcode(true);
        } else {
          setIsValidPostcode(false);
        }
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadOpticians();
  }, [postcode]);

  const columns = React.useMemo(
    () => {
      const allColumns = [
        {
          Header: "Practice Name",
          accessor: "companyname",
        },
        {
          Header: "Address",
          accessor: "FullAddress",
        },
        {
          Header: "Distance (miles)",
          accessor: "DistanceFromAddress",
        },
        {
          Header: "Dispensing",
          accessor: "DispensingFees",
        },
        {
          Header: "Phone",
          accessor: "cotelephone",
        },
        {
          Header: "Favourite",
          accessor: "type",
          Filter: SelectColumnFilter,
          filter: 'includes',
          Cell: ({ row }) => {
            const isFavourite = row.original.type;

            return (
              <Button style={{ fontSize: "12px" }} onClick={() => favouriteOptician(row.original, isFavourite === 'Favourite')}>
                {isFavourite === 'Local' ? 'Favourite' : 'Un-Favourite'}
              </Button>
            );
          }
        }
      ];

      const mobileColumns = allColumns.filter(column => {
        if (isMobile && (column.accessor === 'dispensingFees' || column.accessor === 'distanceFromAddress' || column.accessor === 'cotelephone' || column.accessor === 'Favourite')) {
          return false;
        }
        return true;
      });

      return isMobile ? mobileColumns : allColumns;
    },
    [isMobile]
  );

  async function favouriteOptician(optician, bRemove) {
    setIsLoading(true);
    try {
      if (bRemove) {
        await removeFavouriteOptician(optician.unqID);
      } else {

        const newFavouriteOptician = {
          opticianID: optician.unqID,
          enduser: email,
          enduser_co_name: companyName,
          opticians_onsite_ID: optician.unqID
        }

        await addFavouriteOptician(newFavouriteOptician, accountCode);
      }

      loadOpticians();
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }

  const initialState = { hiddenColumns: ['type'] };

  return (
    <Modal open={isModalOpen}>
      <StyledModal>
        {error && !isLoading && <Alert type="error">Unable to process request due to <b>{error.message}</b></Alert>}
        {isLoading && <Alert type='info'>Loading..</Alert>}
        {!isLoading && !error &&
          <ModalBody>
            <Heading>Add new favourite opticians</Heading>
            <Paragraph>
              Please select an optician from the list below {isMobile ? "" : "and also note that opticians fees are subject to change and out of the control of MCR Safety. We pass through all opticians fees at cost and do not mark up these fees."}</Paragraph>
            <Table
              columns={columns}
              data={opticians}
              initialState={initialState}
              mobilePageSize={5}
              setPostCodeFilter={setPostcode}
              postcodeValue={postcode}
              isValidPc={isValidPostcode} />
          </ModalBody>
        }
        <ModalFooter>
          <CancelButton onClick={showHideModal}>Close</CancelButton>
        </ModalFooter>
      </StyledModal>
    </Modal>
  );
}